/**
 * useColumnResize — Column width management with drag-to-resize and persistence.
 *
 * Features:
 * - Tracks per-column widths overriding the defaults from ColumnConfig
 * - Minimum column width: MIN_COLUMN_WIDTH (50px)
 * - Persists widths to localStorage when storageKey provided
 * - Attaches document-level pointermove/pointerup during active resize (supports mouse + touch)
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** Minimum column width in pixels */
export const MIN_COLUMN_WIDTH = 50;

interface ResizeOp {
  colId: string;
  startX: number;
  startWidth: number;
  pointerId: number;
}

function loadWidths(storageKey: string): Record<string, number> {
  try {
    const stored = localStorage.getItem(`${storageKey}_widths`);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        // Validate that all values are numbers
        const entries = Object.entries(parsed as Record<string, unknown>);
        const valid: Record<string, number> = {};
        for (const [key, val] of entries) {
          if (typeof val === 'number' && Number.isFinite(val)) {
            valid[key] = Math.max(MIN_COLUMN_WIDTH, val);
          }
        }
        return valid;
      }
    }
  } catch {
    // localStorage unavailable or invalid JSON — ignore
  }
  return {};
}

export interface UseColumnResizeResult {
  /** Get effective width for a column (user-resized width takes precedence over default) */
  getColumnWidth: (colId: string, defaultWidth: number) => number;
  /** Begin a resize operation on pointerdown of the resize handle */
  startResize: (
    colId: string,
    startX: number,
    currentWidth: number,
    pointerId: number
  ) => void;
  /** Directly set a column width (for keyboard resize). Respects MIN_COLUMN_WIDTH. */
  setColumnWidth: (colId: string, newWidth: number) => void;
  /** Whether any column is actively being resized */
  isResizing: boolean;
  /** The column ID currently being resized, if any */
  resizingColId: string | null;
}

/**
 * Manages column width state with drag-to-resize and localStorage persistence.
 *
 * @param storageKey - Optional localStorage key prefix for persistence
 */
export function useColumnResize(storageKey?: string): UseColumnResizeResult {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    () => (storageKey ? loadWidths(storageKey) : {})
  );
  const [resizingColId, setResizingColId] = useState<string | null>(null);

  // Refs for stable access inside event handlers without stale closures
  const resizeOpRef = useRef<ResizeOp | null>(null);
  const widthsRef = useRef(columnWidths);
  // Keep widthsRef in sync with state on every render
  widthsRef.current = columnWidths;

  // Resync persisted widths when storageKey changes (e.g. switching entity type)
  const prevStorageKeyRef = useRef(storageKey);
  useEffect(() => {
    if (prevStorageKeyRef.current !== storageKey) {
      prevStorageKeyRef.current = storageKey;
      const next = storageKey ? loadWidths(storageKey) : {};
      widthsRef.current = next;
      setColumnWidths(next);
    }
  }, [storageKey]);

  const getColumnWidth = useCallback(
    (colId: string, defaultWidth: number): number => {
      // widthsRef is a stable ref — the exhaustive-deps rule exempts refs
      return (
        widthsRef.current[colId] ?? Math.max(MIN_COLUMN_WIDTH, defaultWidth)
      );
    },
    []
  );

  const startResize = useCallback(
    (
      colId: string,
      startX: number,
      currentWidth: number,
      pointerId: number
    ) => {
      resizeOpRef.current = {
        colId,
        startX,
        startWidth: currentWidth,
        pointerId,
      };
      setResizingColId(colId);
    },
    []
  );

  const setColumnWidth = useCallback(
    (colId: string, newWidth: number) => {
      const clamped = Math.max(MIN_COLUMN_WIDTH, newWidth);
      const next = { ...widthsRef.current, [colId]: clamped };
      widthsRef.current = next;
      setColumnWidths(next);
      if (storageKey) {
        try {
          localStorage.setItem(`${storageKey}_widths`, JSON.stringify(next));
        } catch {
          // localStorage quota exceeded — ignore
        }
      }
    },
    [storageKey]
  );

  useEffect(() => {
    if (!resizingColId) {
      return;
    }

    const handlePointerMove = (e: PointerEvent) => {
      const op = resizeOpRef.current;
      if (!op || e.pointerId !== op.pointerId) {
        return;
      }
      const dx = e.clientX - op.startX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, op.startWidth + dx);
      const next = { ...widthsRef.current, [op.colId]: newWidth };
      widthsRef.current = next;
      setColumnWidths(next);
    };

    const handlePointerEnd = (e: PointerEvent) => {
      const op = resizeOpRef.current;
      if (op && e.pointerId !== op.pointerId) {
        return;
      }
      resizeOpRef.current = null;
      setResizingColId(null);
      if (storageKey) {
        try {
          localStorage.setItem(
            `${storageKey}_widths`,
            JSON.stringify(widthsRef.current)
          );
        } catch {
          // localStorage quota exceeded — ignore
        }
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerEnd);
      document.addEventListener('pointercancel', handlePointerEnd);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerEnd);
        document.removeEventListener('pointercancel', handlePointerEnd);
      }
    };
  }, [resizingColId, storageKey]);

  return {
    getColumnWidth,
    startResize,
    setColumnWidth,
    isResizing: resizingColId !== null,
    resizingColId,
  };
}
